import React, { useEffect, useRef, useState, useCallback } from 'react';
import { setup } from '@incodetech/core';
import { initializeSession, getFinishStatus } from '@incodetech/core/session';
import { resolveDashboardModuleConfig } from '@incodetech/core/flow';
import { createAuthenticationManager, type AuthenticationManager, type FaceCaptureManagerState } from '@incodetech/core/authentication';
import { resolveWasmConfig, setWasmConfig, warmupWasm, initializeWasmUtil } from '@incodetech/core/wasm';
import { reportEvent } from '@/shared/helpers/report-event.helper';
import { LivenessStep, markLivenessStep } from '@/shared/helpers/liveness-step-tracker.helper';
import { LogType } from '../../../../../../shared/types/logType';
import { INCODE_V2_BIOMETRIC_FAILURE_CODES, INCODE_V2_NON_RETRIABLE_CODES } from '../incodeErrorCodes';
import { describeIncodeError, incodeErrorTitle, isTransientLoadError } from '../incodeErrorDetails';
import { useTranslation } from '../../aws/hooks/useTranslation';
import type { TranslationKey } from '../../aws/translations';

interface IncodeFaceAuthV2Props {
  apiUrl: string;
  identityId: string;
  sessionToken: string;
  callBack: (response: any) => void;
  onConnectionError?: () => void;
  personUuid?: string;
  vendor?: string;
}

// Maps the SDK's detectionStatus to a guidance translation key. Unmapped
// statuses fall back to the default centering hint.
const DETECTION_GUIDANCE_KEYS: Record<string, TranslationKey> = {
  idle: 'LIVENESS_V2_INCODE_GUIDANCE_IDLE',
  detecting: 'LIVENESS_V2_INCODE_GUIDANCE_DETECTING',
  noFace: 'LIVENESS_V2_INCODE_GUIDANCE_NO_FACE',
  tooManyFaces: 'LIVENESS_V2_INCODE_GUIDANCE_TOO_MANY_FACES',
  tooClose: 'LIVENESS_V2_INCODE_GUIDANCE_TOO_CLOSE',
  tooFar: 'LIVENESS_V2_INCODE_GUIDANCE_TOO_FAR',
  blur: 'LIVENESS_V2_INCODE_GUIDANCE_BLUR',
  dark: 'LIVENESS_V2_INCODE_GUIDANCE_DARK',
  tooBright: 'LIVENESS_V2_INCODE_GUIDANCE_TOO_BRIGHT',
  faceAngle: 'LIVENESS_V2_INCODE_GUIDANCE_FACE_ANGLE',
  lookAtCamera: 'LIVENESS_V2_INCODE_GUIDANCE_LOOK_AT_CAMERA',
  headWear: 'LIVENESS_V2_INCODE_GUIDANCE_HEAD_WEAR',
  lenses: 'LIVENESS_V2_INCODE_GUIDANCE_LENSES',
  eyesClosed: 'LIVENESS_V2_INCODE_GUIDANCE_EYES_CLOSED',
  faceMask: 'LIVENESS_V2_INCODE_GUIDANCE_FACE_MASK',
  faceOcclusion: 'LIVENESS_V2_INCODE_GUIDANCE_FACE_OCCLUSION',
  centerFace: 'LIVENESS_V2_INCODE_GUIDANCE_CENTER_FACE',
  moveLeft: 'LIVENESS_V2_INCODE_GUIDANCE_MOVE_LEFT',
  moveRight: 'LIVENESS_V2_INCODE_GUIDANCE_MOVE_RIGHT',
  manualCapture: 'LIVENESS_V2_INCODE_GUIDANCE_MANUAL_CAPTURE',
  getReady: 'LIVENESS_V2_INCODE_GUIDANCE_GET_READY',
  getReadyFinished: 'LIVENESS_V2_INCODE_GUIDANCE_GET_READY_FINISHED',
  capturing: 'LIVENESS_V2_INCODE_GUIDANCE_CAPTURING',
  success: 'LIVENESS_V2_INCODE_GUIDANCE_SUCCESS',
  offline: 'LIVENESS_V2_INCODE_GUIDANCE_OFFLINE',
  error: 'LIVENESS_V2_INCODE_GUIDANCE_ERROR',
};

const DEFAULT_GUIDANCE_KEY: TranslationKey = 'LIVENESS_V2_INCODE_GUIDANCE_DEFAULT';

// Maps terminal SDK error codes to translation keys. Unmapped codes fall back
// to the default pair so a raw SDK/HTTP identifier never leaks into the UI.
const TERMINAL_ERROR_KEYS: Record<string, { titleKey: TranslationKey; bodyKey: TranslationKey }> = {
  USER_IS_NOT_RECOGNIZED: { titleKey: 'LIVENESS_V2_INCODE_ERROR_NOT_RECOGNIZED_TITLE', bodyKey: 'LIVENESS_V2_INCODE_ERROR_NOT_RECOGNIZED_BODY' },
  SPOOF_ATTEMPT_DETECTED: { titleKey: 'LIVENESS_V2_INCODE_ERROR_SPOOF_TITLE', bodyKey: 'LIVENESS_V2_INCODE_ERROR_SPOOF_BODY' },
  NO_MORE_ATTEMPTS: { titleKey: 'LIVENESS_V2_INCODE_ERROR_NO_ATTEMPTS_TITLE', bodyKey: 'LIVENESS_V2_INCODE_ERROR_NO_ATTEMPTS_BODY' },
  NONEXISTENT_CUSTOMER: { titleKey: 'LIVENESS_V2_INCODE_ERROR_NO_CUSTOMER_TITLE', bodyKey: 'LIVENESS_V2_INCODE_ERROR_NO_CUSTOMER_BODY' },
  HINT_NOT_PROVIDED: { titleKey: 'LIVENESS_V2_INCODE_ERROR_HINT_MISSING_TITLE', bodyKey: 'LIVENESS_V2_INCODE_ERROR_HINT_MISSING_BODY' },
};

const DEFAULT_TERMINAL_KEYS: { titleKey: TranslationKey; bodyKey: TranslationKey } = {
  titleKey: 'LIVENESS_V2_INCODE_ERROR_DEFAULT_TITLE',
  bodyKey: 'LIVENESS_V2_INCODE_ERROR_DEFAULT_BODY',
};

const AlertIcon = () => (
  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#FFEB69" />
    <path d="M24 33C28.9706 33 33 28.9706 33 24C33 19.0294 28.9706 15 24 15C19.0294 15 15 19.0294 15 24C15 28.9706 19.0294 33 24 33Z" stroke="#222926" strokeWidth="2" strokeMiterlimit="10" />
    <path d="M24 24.75V19.5" stroke="#222926" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M24 28C24.069 28 24.125 28.056 24.125 28.125C24.125 28.194 24.069 28.25 24 28.25C23.931 28.25 23.875 28.194 23.875 28.125C23.875 28.056 23.931 28 24 28Z" fill="#222926" stroke="#222926" strokeWidth="2" />
  </svg>
);

const CameraIcon = () => (
  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#E8E8E3" />
    <path d="M31.5 31.5H16.5C16.1022 31.5 15.7206 31.342 15.4393 31.0607C15.158 30.7794 15 30.3978 15 30V19.5C15 19.1022 15.158 18.7206 15.4393 18.4393C15.7206 18.158 16.1022 18 16.5 18H19.5L21 15.75H27L28.5 18H31.5C31.8978 18 32.2794 18.158 32.5607 18.4393C32.842 18.7206 33 19.1022 33 19.5V30C33 30.3978 32.842 30.7794 32.5607 31.0607C32.2794 31.342 31.8978 31.5 31.5 31.5Z" stroke="#222926" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M24 27.75C25.864 27.75 27.375 26.239 27.375 24.375C27.375 22.511 25.864 21 24 21C22.136 21 20.625 22.511 20.625 24.375C20.625 26.239 22.136 27.75 24 27.75Z" stroke="#222926" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const VerifyIcon = () => (
  <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0 24C0 10.7452 10.7452 0 24 0C37.2548 0 48 10.7452 48 24C48 37.2548 37.2548 48 24 48C10.7452 48 0 37.2548 0 24Z" fill="#E8E8E3" />
    <path d="M24 27C26.0711 27 27.75 25.3211 27.75 23.25C27.75 21.1789 26.0711 19.5 24 19.5C21.9289 19.5 20.25 21.1789 20.25 23.25C20.25 25.3211 21.9289 27 24 27Z" stroke="#222926" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M17.9814 30.6909C18.5457 29.5794 19.4068 28.6459 20.4691 27.9938C21.5315 27.3418 22.7537 26.9966 24.0002 26.9966C25.2467 26.9966 26.4689 27.3418 27.5312 27.9938C28.5936 28.6459 29.4547 29.5794 30.0189 30.6909" stroke="#222926" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M32.8754 22.5C33.1918 24.38 32.9026 26.3119 32.0494 28.0168C31.1962 29.7217 29.8233 31.1113 28.1288 31.985C26.4343 32.8587 24.5061 33.1713 22.6224 32.8776C20.7387 32.5839 18.9971 31.6992 17.649 30.3511C16.3009 29.003 15.4162 27.2614 15.1225 25.3777C14.8288 23.494 15.1414 21.5657 16.0151 19.8713C16.8888 18.1768 18.2784 16.8039 19.9833 15.9507C21.6882 15.0975 23.6201 14.8083 25.5001 15.1247" stroke="#222926" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M29.25 17.25L30.75 18.75L33.75 15.75" stroke="#222926" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CameraSlashIcon = () => (
  <svg width="64" height="64" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M0 28C0 12.536 12.536 0 28 0C43.464 0 56 12.536 56 28C56 43.464 43.464 56 28 56C12.536 56 0 43.464 0 28Z" fill="#E8E8E3" />
    <path d="M41 22.0001V36.0001C40.9998 36.1952 40.981 36.3898 40.9438 36.5813C40.9276 36.6735 40.8859 36.7592 40.8235 36.8289C40.761 36.8985 40.6803 36.9492 40.5905 36.9753C40.5006 37.0013 40.4053 37.0016 40.3153 36.9762C40.2252 36.9507 40.1442 36.9006 40.0812 36.8313L22.75 17.7601C22.6749 17.6776 22.6296 17.5724 22.6212 17.4612C22.6129 17.35 22.6419 17.2391 22.7037 17.1463L23.1713 16.4451C23.2623 16.3086 23.3855 16.1967 23.53 16.1191C23.6746 16.0415 23.836 16.0006 24 16.0001H32C32.1647 16 32.3268 16.0405 32.4721 16.1182C32.6173 16.1958 32.7411 16.3081 32.8325 16.4451L34.535 19.0001H38C38.7956 19.0001 39.5587 19.3162 40.1213 19.8788C40.6839 20.4414 41 21.2044 41 22.0001ZM38.74 38.3276C38.8302 38.4244 38.9002 38.5382 38.9461 38.6624C38.9919 38.7865 39.0127 38.9185 39.0072 39.0507C39.0017 39.1829 38.97 39.3127 38.9139 39.4326C38.8578 39.5525 38.7785 39.66 38.6806 39.749C38.5827 39.838 38.4681 39.9067 38.3434 39.9511C38.2187 39.9954 38.0865 40.0146 37.9543 40.0075C37.8222 40.0004 37.6928 39.9671 37.5736 39.9096C37.4544 39.8522 37.3478 39.7716 37.26 39.6726L36.6488 39.0001H18C17.2044 39.0001 16.4413 38.684 15.8787 38.1214C15.3161 37.5588 15 36.7957 15 36.0001V22.0001C15 21.2044 15.3161 20.4414 15.8787 19.8788C16.4413 19.3162 17.2044 19.0001 18 19.0001H18.4662L17.26 17.6726C17.1698 17.5757 17.0998 17.4619 17.0539 17.3378C17.0081 17.2137 16.9873 17.0817 16.9928 16.9495C16.9983 16.8173 17.03 16.6874 17.0861 16.5676C17.1422 16.4477 17.2215 16.3402 17.3194 16.2512C17.4173 16.1622 17.5319 16.0935 17.6566 16.0491C17.7813 16.0047 17.9135 15.9855 18.0457 15.9927C18.1778 15.9998 18.3072 16.033 18.4264 16.0905C18.5456 16.148 18.6522 16.2286 18.74 16.3276L38.74 38.3276ZM30.5 32.2401L24.515 25.6551C23.8357 26.4904 23.4784 27.5416 23.508 28.6179C23.5376 29.6941 23.9521 30.7241 24.6763 31.5209C25.4004 32.3176 26.3862 32.8283 27.4548 32.9603C28.5233 33.0922 29.6038 32.8367 30.5 32.2401Z" fill="#222926" />
  </svg>
);

const IdentityIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="8" r="3" stroke="currentColor" strokeWidth="2" />
    <path d="M5.5 19c.8-3.2 3.1-5 6.5-5s5.7 1.8 6.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="7" y="3" width="10" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
    <path d="M10 17h4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SelfiePermissionScreen = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="liveness-v2-selfie-permission">
      <div className="liveness-v2-selfie-permission__content">
        <img className="liveness-v2-selfie-permission__illustration" src={`${process.env.PUBLIC_URL}/assets/camera.svg`} alt="" />
        <h1 className="liveness-v2-selfie-permission__title">{t('LIVENESS_V2_CAMERA_PERMISSION_TITLE')}</h1>
        <div className="liveness-v2-selfie-permission__reason">
          <span className="liveness-v2-selfie-permission__reason-icon"><IdentityIcon /></span>
          <span>{t('LIVENESS_V2_CAMERA_PERMISSION_IDENTITY')}</span>
        </div>
        <div className="liveness-v2-selfie-permission__reason">
          <span className="liveness-v2-selfie-permission__reason-icon"><PhoneIcon /></span>
          <span>{t('LIVENESS_V2_CAMERA_PERMISSION_SIGNATURE')}</span>
        </div>
      </div>
      <button type="button" className="liveness-v2-selfie-permission__button" onClick={onClick}>
        {t('LIVENESS_V2_CAMERA_PERMISSION_BUTTON')}
      </button>
    </div>
  );
};

const CameraBlockedScreen = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();

  return (
    <ActionScreen
      icon={<CameraSlashIcon />}
      title={t('LIVENESS_V2_INCODE_CAMERA_BLOCKED_TITLE')}
      subtitle={t('LIVENESS_V2_INCODE_CAMERA_BLOCKED_BODY')}
      buttonLabel={t('LIVENESS_V2_INCODE_CAMERA_BLOCKED_BUTTON')}
      onClick={onClick}
    />
  );
};

const Spinner = ({ label }: { label: string }) => (
  <div className="liveness-v2-spinner">
    <div className="liveness-v2-spinner__ring" aria-hidden="true" />
    <p className="liveness-v2-spinner__label">{label}</p>
  </div>
);

interface ActionScreenProps {
  title: string;
  subtitle?: string;
  buttonLabel: string;
  onClick: () => void;
  icon?: React.ReactNode;
  isOverlay?: boolean;
}

const ActionScreen = ({ title, subtitle, buttonLabel, onClick, icon, isOverlay }: ActionScreenProps) => (
  <div className={isOverlay ? 'liveness-v2-action liveness-v2-action--overlay' : 'liveness-v2-action'}>
    <div className="liveness-v2-action__card">
      {icon && <div className="liveness-v2-action__icon">{icon}</div>}
      <h2 className="liveness-v2-action__title">{title}</h2>
      {subtitle && <p className="liveness-v2-action__subtitle">{subtitle}</p>}
      <button type="button" onClick={onClick} className="liveness-v2-action__button">
        {buttonLabel}
      </button>
    </div>
  </div>
);

// Shown when auto-capture times out (detectionStatus === 'manualCapture').
// Detection has stopped by then — only manager.capture() advances the flow —
// so the user must trigger the shot themselves. Rendered as an overlay so the
// live preview stays visible behind it for framing.
const ManualCapturePanel = ({ onTakePhoto, onRetryAuto }: { onTakePhoto: () => void; onRetryAuto: () => void }) => {
  const { t } = useTranslation();

  return (
    <div className="liveness-v2-action liveness-v2-action--overlay">
      <div className="liveness-v2-action__card">
        <div className="liveness-v2-action__icon"><CameraIcon /></div>
        <h2 className="liveness-v2-action__title">{t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_TITLE')}</h2>
        <p className="liveness-v2-action__subtitle">{t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_BODY')}</p>
        <ul className="liveness-v2-manual-capture__tips">
          <li>{t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_TIP_LIGHTING')}</li>
          <li>{t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_TIP_ACCESSORIES')}</li>
          <li>{t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_TIP_STILL')}</li>
        </ul>
        <button type="button" onClick={onTakePhoto} className="liveness-v2-action__button">
          {t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_TAKE_PHOTO')}
        </button>
        <button type="button" onClick={onRetryAuto} className="liveness-v2-action__link">
          {t('LIVENESS_V2_INCODE_MANUAL_CAPTURE_RETRY_AUTO')}
        </button>
      </div>
    </div>
  );
};

const IncodeAuthV2 = ({ apiUrl, identityId, sessionToken, callBack, onConnectionError, personUuid, vendor }: IncodeFaceAuthV2Props) => {
  const managerRef = useRef<AuthenticationManager | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hasFinalizedRef = useRef(false);
  const successAdvancedRef = useRef(false);
  const [state, setState] = useState<FaceCaptureManagerState>({ status: 'idle' });
  const [bootError, setBootError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const lastReportedStateKeyRef = useRef<string | null>(null);
  const hasAutoRetriedBootRef = useRef(false);
  const { t } = useTranslation();

  const logError = useCallback((phase: string, extra: { errorMessage?: string; moduleErrorCode?: string | null } & Record<string, unknown>) => {
    reportEvent(incodeErrorTitle(phase, extra), {
      event: 'INCODE_SDK_ERROR',
      vendor,
      engineVersion: '2',
      livenessSessionId: identityId,
      personUuid,
      environment: process.env.NODE_ENV,
      timestamp: new Date().toISOString(),
      phase,
      ...extra,
    }, LogType.ERROR);
  }, [identityId, personUuid, vendor]);

  // Tear down and rebuild the manager on the same session. Used by the manual
  // capture panel's "Try auto-capture again" — bumping retryKey re-runs the
  // boot effect, whose cleanup stops the old manager first. No upload happened,
  // so nothing is consumed; hasFinalizedRef stays false.
  // Reset to 'idle' first: the boot effect stops the old manager synchronously
  // but the new one only emits after an async setup, so without this the stale
  // capture snapshot keeps the frozen video and manual panel on screen (and a
  // second tap would queue another recreation) until the reboot lands.
  const recreateManager = useCallback(() => {
    successAdvancedRef.current = false;
    setState({ status: 'idle' });
    setRetryKey((k) => k + 1);
  }, []);

  // DEBUG (flag rollout, remove before flipping in prod): trace every state
  // transition from the SDK so we can see the real `finished.processResponse`
  // shape and confirm whether/where `candidate` actually lands.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[incode-v2] state', state);
    }
  }, [state]);

  // Generic screen/state-transition telemetry — covers every screen the
  // switch below can render (permissions prompt/denied/learnMore, tutorial,
  // closed, and every capture sub-state including manual-capture fallback
  // and upload errors) from one place, so a new state doesn't need its own
  // reportEvent call to be visible in Datadog.
  useEffect(() => {
    const captureState = state as {
      permissionStatus?: string;
      captureStatus?: string;
      detectionStatus?: string;
      uploadError?: unknown;
    };
    const { permissionStatus, captureStatus, detectionStatus, uploadError } = captureState;
    const key = `${state.status}:${permissionStatus ?? ''}:${captureStatus ?? ''}:${detectionStatus ?? ''}:${uploadError ? 'error' : ''}`;
    if (lastReportedStateKeyRef.current === key) return;
    lastReportedStateKeyRef.current = key;
    reportEvent('LIVENESS_INCODE_STATE_CHANGED', {
      event: 'LIVENESS_INCODE_STATE_CHANGED',
      vendor,
      // Named `incodeStatus`, not `status` — a context key literally named
      // `status` gets merged over Datadog's own severity field (see
      // report-event.helper.ts), which was silently turning routine states
      // like manual-capture into error/critical-status logs.
      incodeStatus: state.status,
      permissionStatus,
      captureStatus,
      detectionStatus,
      hasUploadError: !!uploadError,
      uploadError: typeof uploadError === 'string' ? uploadError : undefined,
      livenessSessionId: identityId,
      personUuid,
      timestamp: new Date().toISOString(),
    }, LogType.INFO);
  }, [state, identityId, personUuid, vendor]);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    const boot = async () => {
      const mountStart = performance.now();
      // Which boot call threw — the error alone rarely says (e.g. a bare
      // "Failed to fetch" could be setup, WASM download or session start).
      let bootStep = 'setup';
      try {
        await setup({
          apiURL: apiUrl,
          // Dev only: skips the DevTools anti-tamper detector, which loops
          // `debugger` statements and freezes local debugging otherwise.
          // Never true in production builds.
          devMode: process.env.NODE_ENV !== 'production',
        });

        // WASM warmup starts here (component mount), same timing as the 1.x
        // script load today — deliberately NOT hoisted earlier (e.g. app
        // boot) in this first flag-gated rollout, since that would mean
        // speculatively downloading a multi-MB WASM/model bundle before we
        // know whether this session even lands on Incode (vs. AWS) or on
        // this engine (vs. 1.x). Revisit hoisting once real rollout data
        // shows what fraction of sessions land here.
        const wasmConfig = resolveWasmConfig({ pipelines: ['selfie'] });
        setWasmConfig(wasmConfig);
        bootStep = 'warmupWasm';
        const wasmReady = warmupWasm(wasmConfig).then(() => initializeWasmUtil());
        // Awaited further down, but a CDN failure can reject it while we're
        // still in initializeSession — without a handler attached now the
        // browser reports it as an unhandled rejection (Safari: "Importing a
        // module script failed.") on top of the boot error. The await below
        // still throws.
        wasmReady.catch(() => {});

        bootStep = 'initializeSession';
        await initializeSession({ token: sessionToken });

        bootStep = 'resolveDashboardModuleConfig';
        const config = await resolveDashboardModuleConfig({ moduleKey: 'AUTHENTICATION' });

        // Top up to the 6-model on-device bundle if the dashboard flow needs
        // it (mirrors getRequiredWasmPipelines' rule for AUTHENTICATION).
        if (config.onDeviceFaceResultsSubmissionEnabled) {
          const onDeviceConfig = resolveWasmConfig({ pipelines: ['onDeviceSelfie'] });
          setWasmConfig(onDeviceConfig);
          warmupWasm(onDeviceConfig).then(() => initializeWasmUtil()).catch((topUpError: unknown) => {
            logError('wasmTopUp', describeIncodeError(topUpError, 'On-device WASM warmup failed'));
          });
        }

        bootStep = 'warmupWasm';
        await wasmReady;

        if (cancelled) return;

        bootStep = 'createAuthenticationManager';
        const manager = createAuthenticationManager({ config, authHint: identityId });
        managerRef.current = manager;
        unsubscribe = manager.subscribe((next) => {
          setState(next);
          // Funnel steps; the tracker reports each once per transaction.
          // CAMERA_STARTED normally comes from the getUserMedia hook first —
          // this is the backstop if the SDK got its stream another way.
          if (next.status === 'capture' && next.stream) {
            markLivenessStep(LivenessStep.CAMERA_STARTED, { cameraSource: 'incode-sdk' });
          }
          if (next.status === 'capture' && next.captureStatus === 'success') {
            markLivenessStep(LivenessStep.CAPTURE_COMPLETED, { engineVersion: '2' });
          }
          // Verified against the reference: some auth captures stall at
          // `capture.success` unless the host nudges the machine to advance.
          if (
            next.status === 'capture'
            && next.captureStatus === 'success'
            && !successAdvancedRef.current
          ) {
            successAdvancedRef.current = true;
            void managerRef.current?.nextStep();
          }
        });
        manager.load();

        reportEvent('INCODE_SDK_TIMING', {
          event: 'INCODE_SDK_TIMING',
          vendor,
          engineVersion: '2',
          livenessSessionId: identityId,
          personUuid,
          environment: process.env.NODE_ENV,
          timestamp: new Date().toISOString(),
          sdkReadyDurationMs: Math.round(performance.now() - mountStart),
        }, LogType.INFO);
      } catch (error) {
        if (cancelled) return;
        const details = describeIncodeError(error, 'Failed to start Incode SDK 2.0');
        // A dropped connection while downloading the SDK/WASM usually works
        // on a second try — retry once silently before showing the error.
        const willAutoRetry = !hasAutoRetriedBootRef.current && isTransientLoadError(error);
        logError('boot', { ...details, bootStep, willAutoRetry });
        if (willAutoRetry) {
          hasAutoRetriedBootRef.current = true;
          setRetryKey((k) => k + 1);
          return;
        }
        setBootError(details.errorMessage);
      }
    };

    void boot();

    return () => {
      cancelled = true;
      if (unsubscribe) unsubscribe();
      managerRef.current?.stop();
      managerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, sessionToken, identityId, retryKey]);

  // Bind the live camera stream to the video element whenever it changes.
  useEffect(() => {
    if (state.status === 'capture' && videoRef.current) {
      videoRef.current.srcObject = state.stream ?? null;
    }
  }, [state]);

  // Finalize exactly once. Verified against @incodetech/core@2.2.0:
  //   - the auth actor only transitions to 'finished' when the upload came
  //     back with overallStatus === 'PASS' (any other overallStatus routes
  //     through setTerminalError → 'error' with a moduleErrorCode);
  //   - orchestrated/headless integrations MUST call getFinishStatus or the
  //     Incode session stays NOT COMPLETED forever and no webhook fires.
  // Because 'finished' implies PASS against the identity we passed as
  // authHint, we can honestly forward `candidate: identityId` — matches
  // V1's observed contract with persons-api (candidate == expectedIdentityId
  // on success). getFinishStatus is fired-and-forgotten purely to finalize
  // the Incode session; persons-api doesn't consume its return value.
  useEffect(() => {
    if (hasFinalizedRef.current) return;

    if (state.status === 'finished') {
      hasFinalizedRef.current = true;
      void getFinishStatus(null).catch((finishError: unknown) => {
        logError('finalize', describeIncodeError(finishError, 'getFinishStatus failed'));
      });
      const payload = { candidate: identityId, error: false };
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[incode-v2] finished.processResponse', state.processResponse);
        // eslint-disable-next-line no-console
        console.log('[incode-v2] callBack payload (success)', payload);
      }
      callBack(payload);
    } else if (state.status === 'error') {
      const moduleErrorCode = state.moduleErrorCode ?? null;
      const isBiometricFailure = moduleErrorCode !== null && INCODE_V2_BIOMETRIC_FAILURE_CODES.includes(moduleErrorCode);
      const isNonRetriable = moduleErrorCode !== null && INCODE_V2_NON_RETRIABLE_CODES.includes(moduleErrorCode);

      logError('capture', { errorMessage: state.error, moduleErrorCode });

      if (!isBiometricFailure && !isNonRetriable && onConnectionError) {
        hasFinalizedRef.current = true;
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[incode-v2] error → onConnectionError (AWS retry)', { moduleErrorCode, error: state.error });
        }
        onConnectionError();
      } else {
        hasFinalizedRef.current = true;
        const payload = {
          error: true,
          errorStatus: null,
          errorCode: moduleErrorCode,
          errorMessage: state.error,
        };
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[incode-v2] callBack payload (error)', payload);
        }
        callBack(payload);
      }
    } else if (state.status === 'closed') {
      // User dismissed the flow via the permission screen's close (X)
      // button — not a technical failure, so no AWS retry. Must still
      // resolve so persons-api doesn't wait on a session that will never
      // finish.
      hasFinalizedRef.current = true;
      const payload = {
        error: true,
        errorStatus: null,
        errorCode: 'USER_CLOSED',
        errorMessage: 'User closed the liveness capture flow',
      };
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[incode-v2] callBack payload (closed)', payload);
      }
      callBack(payload);
    }
  }, [state, callBack, onConnectionError, logError, identityId]);

  if (bootError) {
    return (
      <ActionScreen
        icon={<AlertIcon />}
        title={t('LIVENESS_V2_INCODE_BOOT_ERROR_TITLE')}
        subtitle={t('LIVENESS_V2_INCODE_BOOT_ERROR_BODY')}
        buttonLabel={t('LIVENESS_V2_INCODE_TRY_AGAIN')}
        onClick={() => { successAdvancedRef.current = false; setBootError(null); setRetryKey((k) => k + 1); }}
      />
    );
  }

  switch (state.status) {
    case 'idle':
    case 'loading':
      return <Spinner label={t('LIVENESS_V2_INCODE_LOADING')} />;

    case 'tutorial':
      return (
        <ActionScreen
          icon={<VerifyIcon />}
          title={t('LIVENESS_V2_INCODE_TUTORIAL_TITLE')}
          subtitle={t('LIVENESS_V2_INCODE_TUTORIAL_BODY')}
          buttonLabel={t('LIVENESS_V2_INCODE_TUTORIAL_BUTTON')}
          onClick={() => managerRef.current?.nextStep()}
        />
      );

    case 'permissions':
      switch (state.permissionStatus) {
        case 'denied':
          return <CameraBlockedScreen onClick={() => managerRef.current?.requestPermission()} />;
        case 'learnMore':
          return (
            <ActionScreen
              icon={<CameraIcon />}
              title={t('LIVENESS_V2_INCODE_LEARN_MORE_TITLE')}
              subtitle={t('LIVENESS_V2_INCODE_LEARN_MORE_BODY')}
              buttonLabel={t('LIVENESS_V2_INCODE_LEARN_MORE_BUTTON')}
              onClick={() => managerRef.current?.back()}
            />
          );
        case 'requesting':
        case 'checking':
          return <Spinner label={t('LIVENESS_V2_INCODE_PERMISSION_WAITING')} />;
        default:
          return <SelfiePermissionScreen onClick={() => managerRef.current?.requestPermission()} />;
      }

    case 'capture':
      return (
        <div style={{ position: 'relative', width: '100vw', height: '100vh', backgroundColor: 'black', overflow: 'hidden' }}>
          <style>{`
            @keyframes incode-v2-guide-pulse {
              0%, 100% { opacity: 0.65; }
              50% { opacity: 0.9; }
            }
          `}</style>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            data-dd-privacy="hidden"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              background: '#111',
              transform: state.usingBackCamera ? 'none' : 'scaleX(-1)',
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '15%',
              bottom: '25%',
              left: '15%',
              right: '15%',
              border: '2px dashed rgba(255, 255, 255, 0.75)',
              borderRadius: '50% / 42%',
              pointerEvents: 'none',
              animation: 'incode-v2-guide-pulse 2s ease-in-out infinite',
            }}
          />
          {state.detectionStatus !== undefined && state.detectionStatus !== 'manualCapture' && (
            <div style={{ position: 'absolute', bottom: 40, left: 16, right: 16, textAlign: 'center', color: 'white', fontSize: 16, fontWeight: 600, padding: '10px 16px', backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 12, textShadow: '0 1px 3px rgba(0,0,0,0.7)' }}>
              {t(DETECTION_GUIDANCE_KEYS[state.detectionStatus] ?? DEFAULT_GUIDANCE_KEY)}
            </div>
          )}
          {state.captureStatus === 'detecting' && state.detectionStatus === 'manualCapture' && (
            <ManualCapturePanel
              onTakePhoto={() => managerRef.current?.capture()}
              onRetryAuto={recreateManager}
            />
          )}
          {state.captureStatus === 'uploadError' && (() => {
            const code = state.uploadError ?? '';
            const keys = TERMINAL_ERROR_KEYS[code] ?? DEFAULT_TERMINAL_KEYS;
            return (
              <ActionScreen
                isOverlay
                icon={<AlertIcon />}
                title={t(keys.titleKey)}
                subtitle={t(keys.bodyKey)}
                buttonLabel={t('LIVENESS_V2_INCODE_TRY_AGAIN')}
                onClick={() => { successAdvancedRef.current = false; managerRef.current?.retryCapture(); }}
              />
            );
          })()}
        </div>
      );

    case 'processing':
    case 'finished':
      return <Spinner label={t('LIVENESS_V2_INCODE_PROCESSING')} />;

    case 'closed':
      return null;

    case 'error':
      // Terminal handling already ran in the effect above (onConnectionError
      // or callBack). Show a brief processing state while that unwinds.
      return <Spinner label={t('LIVENESS_V2_INCODE_PROCESSING')} />;

    default:
      return <Spinner label={t('LIVENESS_V2_INCODE_LOADING')} />;
  }
};

export default IncodeAuthV2;
